import './Home.css';
import { loadContent, type Service } from '../data/content';
import { useState, useEffect } from 'react';

interface HomeProps {
  onNavigate?: (page: string) => void;
  isLoggedIn?: boolean;
  userRole?: string;
}

export default function Home({ onNavigate, isLoggedIn }: HomeProps) {
  const content = loadContent();
  const services = content.services;

  const backgroundImages = [
    '/_65de4965-e2b4-45ed-b72a-4b07d20cdc4a.jpeg',
    '/_9d2add7f-a2b8-45bd-b84c-39d15b0db547.jpeg',
    '/_ed0bd6fc-aa9a-4a3d-a987-0e0781592f8f.jpeg',
  ];

  const [currentBgIndex, setCurrentBgIndex] = useState(0);

  const valueBackgroundImages = [
    '/_83234f84-2a61-4953-84ff-558751c0dc67.jpeg',
    '/_96b1b47f-eaf7-429c-bc6f-f781b2ed1bbd.jpeg',
    '/_97331415-34f8-4164-9637-e79958168f09.jpeg',
  ];

  const [currentValueBgIndex, setCurrentValueBgIndex] = useState(0);

  const servicesBackgroundImages = [
    '/_998d323c-0488-4c4d-bed1-c61637dffad1.jpeg',
    '/_a45b0a31-369e-42e4-a665-8377f018fb25.jpeg',
    '/_a82aefde-f91d-47ea-9793-31d9a49c6dd8.jpeg',
  ];

  const [currentServicesBgIndex, setCurrentServicesBgIndex] = useState(0);

  const userTypesBackgroundImages = [
    '/_41c79663-c929-4e1d-8a1b-ea3d18b7e233.jpeg',
    '/_4f8e93c5-a1c1-47b4-ba65-0265bd75a837.jpeg',
    '/_ab124add-32e8-4c2c-bb54-98d68fb926de.jpeg',
  ];

  const [currentUserTypesBgIndex, setCurrentUserTypesBgIndex] = useState(0);

  // Preload first image of each slideshow for instant display
  useEffect(() => {
    const preloadImages = [
      backgroundImages[0],
      valueBackgroundImages[0],
      servicesBackgroundImages[0],
      userTypesBackgroundImages[0],
    ];

    preloadImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentValueBgIndex((prev) => (prev + 1) % valueBackgroundImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentServicesBgIndex((prev) => (prev + 1) % servicesBackgroundImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentUserTypesBgIndex((prev) => (prev + 1) % userTypesBackgroundImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll('.service-compact-card');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  // Lazy load remaining slideshow images after 1 second (defer non-critical images)
  useEffect(() => {
    const timer = setTimeout(() => {
      const allImages = [
        ...backgroundImages.slice(1), // Skip first (already preloaded)
        ...valueBackgroundImages.slice(1),
        ...servicesBackgroundImages.slice(1),
        ...userTypesBackgroundImages.slice(1),
      ];

      allImages.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }, 1000); // Defer 1 second to prioritize critical content

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background-slideshow">
          {backgroundImages.map((img, index) => (
            <div
              key={img}
              className={`hero-bg-slide ${index === currentBgIndex ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${img})`,
              }}
            />
          ))}
        </div>
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <h1>Connecting Clinical Trial Expertise Across South Africa</h1>
            <p className="hero-subtitle">
              Clinical Trial Expertise - Your trusted platform for clinical trial collaboration,
              connecting consultants, companies, and sites to deliver compliant, high-quality
              research.
            </p>
            <div className="hero-buttons">
              {isLoggedIn ? (
                <>
                  <button
                    className="cta-button primary"
                    onClick={() => onNavigate?.('marketplace')}
                  >
                    Browse Jobs
                  </button>
                  <button
                    className="cta-button secondary"
                    onClick={() => onNavigate?.('consultants')}
                  >
                    Find Consultants
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="cta-button primary"
                    onClick={() => onNavigate?.('consultant-register')}
                  >
                    Consultant Registration
                  </button>
                  <button className="cta-button secondary" onClick={() => onNavigate?.('register')}>
                    Get Started
                  </button>
                  <button className="cta-button secondary" onClick={() => onNavigate?.('login')}>
                    Sign In
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="value-section">
        <div className="value-background-slideshow">
          {valueBackgroundImages.map((img, index) => (
            <div
              key={img}
              className={`value-bg-slide ${index === currentValueBgIndex ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${img})`,
              }}
            />
          ))}
        </div>
        <div className="value-overlay"></div>
        <div className="container">
          <div className="section-header">
            <h2>Why Choose Clinical Trial Expertise</h2>
            <p>A comprehensive platform designed for clinical research excellence</p>
          </div>

          <div className="value-grid">
            <div className="value-card">
              <div className="value-icon search-icon">
                <img
                  src="/mag.png"
                  alt="Search"
                  width="48"
                  height="48"
                  loading="lazy"
                  style={{
                    objectFit: 'contain',
                    marginTop: '0.75rem',
                  }}
                />
              </div>
              <h3>Find Expert Consultants</h3>
              <p>Access verified clinical trial professionals with proven track records</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <img
                  src="/tick box icon.webp"
                  alt="Checkmark"
                  style={{
                    width: '3.78rem',
                    height: '3.78rem',
                    objectFit: 'contain',
                    marginTop: '0.75rem',
                  }}
                />
              </div>
              <h3>SAHPRA Compliant</h3>
              <p>All consultants trained in local regulations and international guidelines</p>
            </div>
            <div className="value-card">
              <div className="value-icon fast-icon">⚡</div>
              <h3>Fast Connections</h3>
              <p>Post jobs and connect with qualified professionals within days</p>
            </div>
            <div className="value-card">
              <div className="value-icon">🛡️</div>
              <h3>Secure Platform</h3>
              <p>Protected communication and verified user profiles</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-section">
        <div className="services-background-slideshow">
          {servicesBackgroundImages.map((img, index) => (
            <div
              key={img}
              className={`services-bg-slide ${index === currentServicesBgIndex ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${img})`,
              }}
            />
          ))}
        </div>
        <div className="services-overlay"></div>
        <div className="container">
          <h2>Expert Services We Offer</h2>
          <p className="services-quality-intro">
            All our services are delivered by GCP-certified professionals with proven track records
            in clinical trial excellence, ensuring the highest standards of quality, compliance, and
            regulatory adherence.
          </p>
          <div className="services-compact-grid">
            {services.map((service: Service, i: number) => (
              <div className="service-compact-card" key={i}>
                <h3>{service.title}</h3>
                <ul className="service-points">
                  {service.points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <button
            className="view-all-btn"
            onClick={() => onNavigate?.(isLoggedIn ? 'consultants' : 'login')}
          >
            {isLoggedIn ? 'View All Consultants →' : 'Login to Explore →'}
          </button>
        </div>
      </section>

      {/* User Types Section */}
      <section className="user-types-section">
        <div className="user-types-background-slideshow">
          {userTypesBackgroundImages.map((img, index) => (
            <div
              key={index}
              className={`user-types-bg-slide ${index === currentUserTypesBgIndex ? 'active' : ''}`}
              style={{ backgroundImage: `url(${img})` }}
            />
          ))}
        </div>
        <div className="user-types-overlay"></div>
        <div className="container">
          <h2>Who We Serve</h2>
          <div className="user-types-grid">
            <div className="user-type-card">
              <div className="user-icon">
                <img
                  src="/proper conultant icon.png"
                  alt="Consultant"
                  className="consultant-icon"
                  width="120"
                  height="120"
                  loading="lazy"
                />
              </div>
              <h3>Consultants</h3>
              <p>Find opportunities, showcase expertise, grow your career</p>
              <button
                className="user-cta"
                onClick={() => onNavigate?.(isLoggedIn ? 'consultants' : 'register')}
              >
                {isLoggedIn ? 'Browse Jobs' : 'Register Now'}
              </button>
            </div>
            <div className="user-type-card">
              <div className="user-icon">
                <img
                  src="/CRO icon.png"
                  alt="Companies & CROs"
                  className="cro-icon"
                  width="120"
                  height="120"
                  loading="lazy"
                />
              </div>
              <h3>Companies & CROs</h3>
              <p>Post jobs, find qualified professionals, manage projects</p>
              <button
                className="user-cta"
                onClick={() => onNavigate?.(isLoggedIn ? 'marketplace' : 'register')}
              >
                {isLoggedIn ? 'Post a Job' : 'Get Started'}
              </button>
            </div>
            <div className="user-type-card">
              <div className="user-icon">
                <img
                  src="/clinical trial site.png"
                  alt="Clinical Trial Sites"
                  className="site-icon"
                  width="120"
                  height="120"
                  loading="lazy"
                />
              </div>
              <h3>Clinical Trial Sites</h3>
              <p>Join FREE, connect with sponsors, showcase capabilities</p>
              <button
                className="user-cta"
                onClick={() => onNavigate?.(isLoggedIn ? 'sites' : 'register')}
              >
                {isLoggedIn ? 'View Sites' : 'Join Free'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <h2>Ready to Transform Your Clinical Trial Success?</h2>
          <p>Join South Africa's leading platform for clinical trial collaboration</p>
          <div className="cta-buttons">
            <button className="btn-secondary" onClick={() => onNavigate?.('contact')}>
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
